import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  useAddSavingsPlanSchema,
  type TAddSavingsPlanSchema,
} from "@features/AddSavingsPlanModal/useAddSavingsPlanSchema";
import { bankCardMetadata } from "@features/Budget/budgetConstants";
import { useBudgetFunctionalityDisabledError } from "@features/Budget/hooks/useBudgetFunctionalityDisabledError";
import { Button } from "@components/Button";
import { BinIcon, PencilIcon } from "@components/icons";
import { FormDatepicker } from "@components/inputs/form/FormDatepicker/FormDatepicker";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import {
  useModifySavingsPlanMutation,
  useRemoveSavingsPlanMutation,
} from "@services/budget/mutations";
import {
  SavingsPlanDataProviderType,
  type ISavingsPlan,
  type ISavingsPlanBankAccountsResponse,
  type TModifySavingsPlanRequest,
} from "@services/budget/types";
import { formatDate, parseDecimalNumberInputValue } from "@utils/units";

interface IEditSavingsPlanModalProps extends IUseModalProps {
  savingsPlan: ISavingsPlan;
  savingsPlanBankAccounts: ISavingsPlanBankAccountsResponse;
}

export const EditSavingsPlanModal: React.FC<IEditSavingsPlanModalProps> = (
  props,
) => {
  const { savingsPlan, savingsPlanBankAccounts, ...rest } = props;
  const { t } = useTranslation();
  const modal = useModal(rest);

  const { mutateAsync, isPending } = useModifySavingsPlanMutation();
  const { mutateAsync: removeSaving, isPending: isRemovePending } =
    useRemoveSavingsPlanMutation();

  const bankSelectOptions = [
    ...savingsPlanBankAccounts.bankAccounts.map((bank) => ({
      label: bank.iban,
      value: bank.bankAccountId,
      icon: bankCardMetadata[bank.bankName].iconSmall,
    })),
  ];

  const schema = useAddSavingsPlanSchema();

  const { control, setValue, getValues, handleSubmit, setError, watch } =
    useForm<TAddSavingsPlanSchema>({
      resolver: zodResolver(schema),
      defaultValues: {
        planName: savingsPlan.plan.planName,
        planAmount: savingsPlan.plan.planAmount,
        achievedAmount: savingsPlan.plan.achievedAmount,
        endDate: savingsPlan.plan.endDate
          ? new Date(savingsPlan.plan.endDate)
          : undefined,
        bankAccountId: savingsPlan.bankAccount?.bankAccountId,
        savingsPlanDataProviderTypeId:
          savingsPlan.plan.savingsPlanDataProviderTypeId,
      },
    });

  const savingsPlanDataProviderTypeId = watch("savingsPlanDataProviderTypeId");
  const bankAccountId = watch("bankAccountId");

  const currentBankIcon =
    bankCardMetadata[
      savingsPlanBankAccounts.bankAccounts.find(
        (bank) => bank.bankAccountId === bankAccountId,
      )?.bankName ?? "Default"
    ]?.iconSmall;

  const providerTypeOptions = [
    {
      label: t("budget.savings.savingsPlanDataProviderType.manual"),
      value: SavingsPlanDataProviderType.Manual,
    },
    {
      label: t("budget.savings.savingsPlanDataProviderType.bankAccountBalance"),
      value: SavingsPlanDataProviderType.BankAccountBalance,
    },
  ];

  const { setApiErrors, toastApiError } = useApiErrorUtils(setError);
  const { handleBudgetError } = useBudgetFunctionalityDisabledError();

  const onSubmit: SubmitHandler<TAddSavingsPlanSchema> = async (data) => {
    const createSavingsPlanRequest = (
      data: TAddSavingsPlanSchema,
    ): TModifySavingsPlanRequest => {
      if (
        data.savingsPlanDataProviderTypeId ===
        SavingsPlanDataProviderType.BankAccountBalance
      ) {
        return {
          savingsPlanId: savingsPlan.savingsPlanId,
          planName: data.planName,
          planAmount: parseDecimalNumberInputValue(data.planAmount.toString()),
          endDate: data.endDate
            ? formatDate(data.endDate, "yyyy-MM-dd")
            : undefined,
          savingsPlanDataProviderTypeId:
            SavingsPlanDataProviderType.BankAccountBalance,
          bankAccountId: data.bankAccountId!,
        };
      } else {
        return {
          savingsPlanId: savingsPlan.savingsPlanId,
          planName: data.planName,
          planAmount: parseDecimalNumberInputValue(data.planAmount.toString()),
          endDate: data.endDate
            ? formatDate(data.endDate, "yyyy-MM-dd")
            : undefined,
          savingsPlanDataProviderTypeId: SavingsPlanDataProviderType.Manual,
          achievedAmount: parseDecimalNumberInputValue(
            data.achievedAmount!.toString(),
          ),
        };
      }
    };

    const response = await mutateAsync(createSavingsPlanRequest(data)).catch(
      handleBudgetError(setApiErrors),
    );

    if (response) {
      toast.success(t("budget.savings.editSavingsPlanModal.success"));
      modal.close();
    }
  };

  const onRemove = async () => {
    const response = await removeSaving({
      savingsPlanId: savingsPlan.savingsPlanId,
    }).catch(handleBudgetError(toastApiError));

    if (response) {
      toast.success(t("budget.savings.editSavingsPlanModal.remove.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      title={t("budget.savings.editSavingsPlanModal.title")}
      badgeProps={{ icon: PencilIcon, color: "green" }}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
          >
            {t("common.form.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <FormInput
          control={control}
          name="planName"
          label={t("budget.savings.editSavingsPlanModal.planName")}
        />
        <FormInput
          control={control}
          name="planAmount"
          label={t("budget.savings.editSavingsPlanModal.planAmmount")}
          onBlur={() =>
            setValue(
              "planAmount",
              parseDecimalNumberInputValue(getValues("planAmount").toString()),
            )
          }
        />
        <FormDatepicker
          control={control}
          name="endDate"
          label={t("budget.savings.editSavingsPlanModal.endDate")}
        />
        <FormSelect
          name="savingsPlanDataProviderTypeId"
          control={control}
          label={t(
            "budget.savings.addSavingsPlanModal.savingsPlanDataProviderType",
          )}
          options={providerTypeOptions}
        />
        {savingsPlanDataProviderTypeId ===
          SavingsPlanDataProviderType.Manual && (
          <FormInput
            control={control}
            name="achievedAmount"
            label={t("budget.savings.editSavingsPlanModal.achievedAmount")}
            onBlur={() =>
              setValue(
                "achievedAmount",
                parseDecimalNumberInputValue(
                  getValues("achievedAmount")?.toString() ?? "0",
                ),
              )
            }
          />
        )}
        {savingsPlanDataProviderTypeId ===
          SavingsPlanDataProviderType.BankAccountBalance && (
          <FormSelect
            label={t("budget.savings.addSavingsPlanModal.bankAccount")}
            name="bankAccountId"
            control={control}
            options={bankSelectOptions}
            icon={currentBankIcon}
          />
        )}
        <button
          className="flex w-fit items-center gap-2 pt-2 text-sm font-semibold text-red-dark"
          disabled={isRemovePending}
          onClick={onRemove}
        >
          <BinIcon className="size-5 stroke-2" />
          {t("budget.savings.editSavingsPlanModal.remove.button")}
        </button>
      </div>
    </Modal>
  );
};
